import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ExternalLink, Users, Shield, Lightbulb, GraduationCap, Building } from 'lucide-react';

export function ResourcesPage() {
  const resources = {
    universities: [
      {
        title: "The Ethics of Artificial Intelligence",
        description: "An interdisciplinary course exploring the moral and social implications of AI systems. Covers algorithmic bias, privacy, transparency, accountability, and the future of human-AI interaction. Includes case studies, ethical frameworks, and practical guidelines for responsible AI development.",
        url: "https://canvas.mit.edu/courses/21388",
        institution: "MIT"
      },
      {
        title: "Human-Centered AI Policy & Governance",
        description: "Stanford's Institute for Human-Centered AI provides research-based policy recommendations and governance frameworks. Features white papers on AI regulation, educational policy, and ethical guidelines for developing AI systems that augment human capabilities while maintaining human agency and dignity.",
        url: "https://hai.stanford.edu/policy",
        institution: "Stanford"
      },
      {
        title: "Future of Humanity Institute - AI Governance",
        description: "Oxford's leading research center on AI governance and existential risk. Provides comprehensive analysis of AI policy challenges, international cooperation frameworks, and long-term safety considerations. Essential reading for understanding global AI governance trends and emerging regulatory approaches.",
        url: "https://www.governance.ai/",
        institution: "Oxford"
      }
    ],
    policies: [
      {
        title: "Recommendation on the Ethics of Artificial Intelligence",
        description: "The first global standard on AI ethics adopted by UNESCO's 193 Member States. Establishes shared values and principles for ensuring AI technologies benefit humanity. Covers human rights, environmental protection, transparency, and the need for impact assessments in AI development and deployment.",
        url: "https://www.unesco.org/en/artificial-intelligence/recommendation-ethics",
        institution: "UNESCO"
      },
      {
        title: "European Union Artificial Intelligence Act",
        description: "The world's first comprehensive AI regulation establishing rules for AI systems based on their potential to cause harm. Creates prohibited practices, high-risk system requirements, and transparency obligations. Includes detailed implementation guidelines and compliance frameworks for organizations deploying AI in the EU.",
        url: "https://artificialintelligenceact.eu/",
        institution: "European Union"
      },
      {
        title: "AI Risk Management Framework (AI RMF 1.0)",
        description: "A comprehensive, flexible framework for managing AI risks across organizations and sectors. Provides practical guidance for identifying, assessing, and mitigating AI risks throughout the system lifecycle. Includes implementation guidance, measurement tools, and alignment with existing risk management practices.",
        url: "https://www.nist.gov/itl/ai-risk-management-framework",
        institution: "NIST"
      }
    ],
    toolkits: [
      {
        title: "Open Source Educator Toolkit",
        description: "Comprehensive curriculum and teaching resources designed to make AI education accessible and inclusive for K-12 students. Features lesson plans, activities, assessment rubrics, and professional development materials. Emphasizes equity, ethics, and hands-on learning experiences that demystify AI concepts.",
        url: "https://ai-4-all.org/open-source/educator-toolkit/",
        institution: "AI4ALL"
      },
      {
        title: "AI for Oceans & Machine Learning for Good",
        description: "Interactive curriculum modules that teach AI concepts through engaging, purpose-driven projects. Students learn about machine learning, neural networks, and ethical AI while working on real-world problems like ocean conservation. Includes teacher guides, student worksheets, and assessment tools for grades 6-12.",
        url: "https://code.org/ai",
        institution: "Code.org"
      },
      {
        title: "ISTE Standards for AI in Education",
        description: "Professional standards and guidance for integrating AI technologies in educational settings. Addresses student learning outcomes, educator competencies, and institutional readiness for AI adoption. Includes research-based recommendations, implementation strategies, and ethical considerations for K-12 and higher education.",
        url: "https://www.iste.org/areas-of-focus/AI-in-education",
        institution: "ISTE"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>AI Education Resources - Curated Learning Materials | Promptliteracy.ai</title>
        <meta name="description" content="Discover curated AI education resources from top universities, policy frameworks from UNESCO and EU, and comprehensive educator toolkits. Essential materials for AI literacy education." />
        
        {/* Open Graph */}
        <meta property="og:title" content="AI Education Resources - Curated Learning Materials | Promptliteracy.ai" />
        <meta property="og:description" content="Discover curated AI education resources from top universities, policy frameworks, and comprehensive educator toolkits for AI literacy education." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://promptliteracy.ai/resources" />
        <meta property="og:site_name" content="Promptliteracy.ai" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="AI Education Resources - Curated Learning Materials" />
        <meta name="twitter:description" content="Curated AI education resources from top universities, policy frameworks, and educator toolkits for AI literacy." />
        
        {/* Additional SEO */}
        <meta name="keywords" content="AI education resources, university AI courses, AI policy frameworks, educator toolkits, UNESCO AI ethics, EU AI Act, NIST AI framework, teaching materials" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://promptliteracy.ai/resources" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "AI Education Resources",
            "description": "Curated collection of AI education resources from universities, policy organizations, and educator toolkits",
            "url": "https://promptliteracy.ai/resources",
            "mainEntity": {
              "@type": "ItemList",
              "name": "AI Education Resource Collection",
              "description": "High-quality educational resources for AI literacy and prompt engineering",
              "numberOfItems": 9
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="py-16 bg-primary text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-4">Educational Resources</h1>
            <p className="text-xl opacity-90">
              Curated links to high-quality university guides, policy frameworks, and educator toolkits
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-primary/3 relative overflow-hidden">
        <div className="absolute inset-0 circuit-pattern opacity-10" />
        <div className="container relative z-10">
        {/* Resource Categories */}
        <div className="space-y-12">
          {/* University Resources */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center border border-primary/20">
                <GraduationCap className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-primary">University Resources</h2>
                <p className="text-muted-foreground">Academic courses and research from leading institutions</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.universities.map((resource, index) => (
                <Card key={index} className="card-slate hover:shadow-lg transition-all hover:scale-[1.02] border-l-4 border-l-primary/30 hover:border-l-primary group card-accent">
                  <CardHeader>
                    <CardTitle className="text-lg leading-tight text-primary">{resource.title}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <Building className="h-4 w-4" />
                      <span className="font-medium">{resource.institution}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                      {resource.description}
                    </p>
                    <Button variant="outline" size="sm" asChild className="w-full border-slate-300 btn-blue-hover transition-colors">
                      <a href={resource.url} target="_blank" rel="noopener noreferrer">
                        View Resource
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Policy Resources */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center border border-secondary/20">
                <Shield className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-primary">Policy & Governance</h2>
                <p className="text-muted-foreground">Global frameworks and regulations for responsible AI</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.policies.map((resource, index) => (
                <Card key={index} className="card-slate hover:shadow-lg transition-all hover:scale-[1.02] border-l-4 border-l-secondary/30 hover:border-l-secondary group card-accent">
                  <CardHeader>
                    <CardTitle className="text-lg leading-tight text-primary">{resource.title}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-secondary">
                      <Shield className="h-4 w-4" />
                      <span className="font-medium">{resource.institution}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                      {resource.description}
                    </p>
                    <Button variant="outline" size="sm" asChild className="w-full border-slate-300 btn-blue-hover transition-colors">
                      <a href={resource.url} target="_blank" rel="noopener noreferrer">
                        View Resource
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Educator Toolkits */}
          <section>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center border border-primary/30">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-primary">Educator Toolkits</h2>
                <p className="text-muted-foreground">Practical resources for teaching AI literacy</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {resources.toolkits.map((resource, index) => (
                <Card key={index} className="card-slate hover:shadow-lg transition-all hover:scale-[1.02] border-l-4 border-l-primary/50 hover:border-l-primary group card-accent">
                  <CardHeader>
                    <CardTitle className="text-lg leading-tight text-primary">{resource.title}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <Users className="h-4 w-4" />
                      <span className="font-medium">{resource.institution}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                      {resource.description}
                    </p>
                    <Button variant="outline" size="sm" asChild className="w-full border-slate-300 btn-blue-hover transition-colors">
                      <a href={resource.url} target="_blank" rel="noopener noreferrer">
                        View Resource
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        </div>

        {/* Submission Notice */}
        <Card className="mt-12 border-primary/20 bg-primary/5 card-accent">
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Lightbulb className="h-6 w-6 text-primary" />
              <h3 className="text-xl font-bold">Know a Great Resource?</h3>
            </div>
            <p className="text-muted-foreground mb-6">
              Help us expand our collection! If you know of high-quality educational resources 
              that should be included here, we'd love to hear from you.
            </p>
            <Button variant="outline" asChild className="border-slate-300 btn-blue-hover transition-colors">
              <a href="/contact">
                Suggest a Resource
              </a>
            </Button>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <Card className="mt-6 card-accent">
          <CardContent className="p-6">
            <h4 className="font-semibold mb-2">Disclaimer</h4>
            <p className="text-sm text-muted-foreground">
              External links are provided for convenience and educational purposes. PromptLiteracy.ai 
              does not endorse or take responsibility for the content of external websites. 
              Please review each resource's terms of use and privacy policies.
            </p>
          </CardContent>
        </Card>
        </div>
      </section>
    </>
  );
}